import { useState } from 'preact/hooks'

export type SortDirection = 'asc' | 'desc' | null
export type SortConfig<T extends string> = {
  key: T
  direction: SortDirection
}

export function useTableSort<T extends string>(defaultKey?: T, defaultDirection: SortDirection = 'asc') {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(
    defaultKey ? { key: defaultKey, direction: defaultDirection } : null
  )

  const requestSort = (key: T) => {
    let direction: SortDirection = 'asc'
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc'
      } else if (sortConfig.direction === 'desc') {
        direction = null
      }
    }
    setSortConfig(direction ? { key, direction } : null)
  }

  const getSortIndicator = (key: T) => {
    if (!sortConfig || sortConfig.key !== key) {
      return null
    }
    return sortConfig.direction
  }

  return { sortConfig, requestSort, getSortIndicator }
}

export function sortData<T>(
  data: T[],
  sortConfig: SortConfig<string> | null,
  getComparator: (key: string) => (a: T, b: T) => number
): T[] {
  if (!sortConfig || !sortConfig.direction) {
    return data
  }

  const comparator = getComparator(sortConfig.key)
  const sorted = [...data].sort(comparator)
  
  if (sortConfig.direction === 'desc') {
    return sorted.reverse()
  }
  
  return sorted
}
